import type { ReactElement } from 'react';
import { CloudDownload } from 'lucide-react';
import type { AgentUpdateState } from '@kirin/api-client/contracts/agent-auto-update';
import { AgentVersionBadge } from './AgentVersionBadge';
import { AgentUpdateStateBadge } from './AgentUpdateStateBadge';
import {
  isAgentVersionOutdated,
  showsUpdateAvailableWith,
} from './agentVersion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface AgentVersionWithUpdateProps {
  version: string | null | undefined;
  /**
   * The real latest GA version (from `useLatestReleases` → `/api/releases/latest`).
   * Freshness is judged against THIS, not the dashboard build's
   * `VITE_VSIX_VERSION`, so a version ahead of GA isn't wrongly flagged.
   */
  latestVersion: string | null | undefined;
  /**
   * KIR-5191: the automatic-update state. Omit (or pass `null`) where the
   * `agent_auto_update` flag is off or the state is unknown: the badge then
   * behaves exactly as before.
   */
  updateState?: AgentUpdateState | null;
  className?: string;
}

/**
 * KIR-2962: an agent version plus an amber "Update available" pill when it's
 * older than the real latest release. Shared by the Inventory → Users DataTable
 * cell and the endpoints dialog so both judge freshness against the same GA
 * source and render the badge identically. Never nags on uncertainty (unknown
 * version or unknown GA → no pill).
 */
export function AgentVersionWithUpdate({
  version,
  latestVersion,
  updateState = null,
  className,
}: AgentVersionWithUpdateProps): ReactElement {
  const outdated = isAgentVersionOutdated(version, latestVersion);
  // KIR-5191: an "Up to date" state older than the latest release only means
  // the agent has not checked since that release — show "Update available"
  // alone. The rest of the rule is shared with the Endpoints list.
  const showUpdateAvailable = outdated && showsUpdateAvailableWith(updateState);
  const showState =
    updateState !== null && !(updateState === 'up_to_date' && outdated);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <AgentVersionBadge
        version={version}
        currentVersion={latestVersion}
        className={className}
      />
      {showUpdateAvailable && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help items-center gap-1 whitespace-nowrap rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              <CloudDownload className="h-3 w-3" />
              Update available
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-60">
            A newer Kirin release is available — the latest is v{latestVersion},
            this is on v{version}.
          </TooltipContent>
        </Tooltip>
      )}
      {showState && <AgentUpdateStateBadge state={updateState} />}
    </span>
  );
}

AgentVersionWithUpdate.displayName = 'AgentVersionWithUpdate';
